import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { AdminRecord } from './types';
import { ADMIN_ROLES, roleLabel } from './types';
import AdminDetailHeader from './components/AdminDetailHeader';
import AdminPersonalSection from './components/AdminPersonalSection';
import AdminAccessSection from './components/AdminAccessSection';
import AdminModulePermissions from './components/AdminModulePermissions';
import AdminConvertToUserSection from './components/AdminConvertToUserSection';
import OverviewTab from '../clientes/components/OverviewTab';
import SubscriptionTab from '../clientes/components/SubscriptionTab';
import PaymentsTab from '../clientes/components/PaymentsTab';
import MarketingTab from '../clientes/components/MarketingTab';
import TimelineTab from '../clientes/components/TimelineTab';
import type { ClientProfile, SubscriptionData, PlanData, PaymentRecord, UsageStats } from '../clientes/types';

type Tab = 'dados' | 'acesso' | 'visao-geral' | 'assinatura' | 'pagamentos' | 'auditoria';

const AdminAdministradorDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [admin, setAdmin] = useState<AdminRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('dados');

  // Client-like data
  const [clientProfile, setClientProfile] = useState<ClientProfile | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const { data: roleRow } = await supabase
      .from('user_roles')
      .select('id, user_id, role, status, invited_by, invited_at, created_at, module_permissions')
      .eq('user_id', id)
      .in('role', [...ADMIN_ROLES])
      .maybeSingle();

    if (!roleRow) { setNotFound(true); setLoading(false); return; }

    const { data: p } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    const profile = p || {} as any;

    setAdmin({
      roleId: roleRow.id,
      userId: roleRow.user_id,
      role: roleRow.role,
      status: roleRow.status || 'active',
      invitedBy: roleRow.invited_by,
      invitedAt: roleRow.invited_at,
      createdAt: roleRow.created_at,
      full_name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.full_name || profile.name || '—',
      first_name: profile.first_name || null,
      last_name: profile.last_name || null,
      display_name: profile.display_name || null,
      email: profile.email || '—',
      phone: profile.phone || null,
      cpf: profile.cpf || null,
      avatar_url: profile.avatar_url || null,
      cep: profile.cep || null,
      street: profile.street || null,
      street_number: profile.street_number || null,
      complement: profile.complement || null,
      neighborhood: profile.neighborhood || null,
      city: profile.city || null,
      state: profile.state || null,
      country: profile.country || null,
      last_seen_at: profile.last_seen_at || null,
      module_permissions: roleRow.module_permissions ?? null,
    });

    // Build ClientProfile for reuse with client tabs
    setClientProfile(profile as ClientProfile);

    // Load subscription
    const { data: sub } = await supabase
      .from('subscriptions')
      .select('id, plan_id, status, subscription_type, amount, billing_cycle, started_at, expires_at, auto_renew, plans(id, name, slug)')
      .eq('user_id', id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (sub) {
      const planInfo = (sub as any).plans;
      setSubscription({
        id: sub.id,
        plan_id: sub.plan_id,
        plan_name: planInfo?.name || '—',
        plan_slug: planInfo?.slug || '—',
        status: sub.status,
        subscription_type: sub.subscription_type,
        amount: sub.amount,
        billing_cycle: sub.billing_cycle,
        started_at: sub.started_at,
        expires_at: sub.expires_at,
        auto_renew: sub.auto_renew,
      });
    }

    // Load plans
    const { data: plansData } = await supabase
      .from('plans')
      .select('*')
      .eq('is_active', true)
      .order('price_brl', { ascending: true });
    setPlans((plansData || []) as PlanData[]);

    // Load payments
    const { data: pays } = await supabase
      .from('payments')
      .select('id, amount, status, payment_method, gateway, gateway_transaction_id, paid_at, created_at, notes, gateway_response, plans(name)')
      .eq('user_id', id)
      .order('created_at', { ascending: false })
      .limit(50);
    setPayments(((pays || []) as any[]).map((pay) => ({ ...pay, plan_name: pay.plans?.name || null })));

    // Load usage stats
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
    const [accRes, cardRes, goalRes, txRes, lastTxRes] = await Promise.all([
      supabase.from('accounts').select('*', { count: 'exact', head: true }).eq('user_id', id),
      supabase.from('cards').select('*', { count: 'exact', head: true }).eq('user_id', id),
      supabase.from('goals').select('*', { count: 'exact', head: true }).eq('user_id', id),
      supabase.from('transactions').select('*', { count: 'exact', head: true }).eq('user_id', id).gte('transaction_date', startOfMonth).lte('transaction_date', endOfMonth),
      supabase.from('transactions').select('transaction_date').eq('user_id', id).order('transaction_date', { ascending: false }).limit(1).maybeSingle(),
    ]);
    setUsage({
      accounts: accRes.count ?? 0,
      cards: cardRes.count ?? 0,
      goals: goalRes.count ?? 0,
      monthly_transactions: txRes.count ?? 0,
      last_transaction_date: (lastTxRes.data as any)?.transaction_date || null,
    });

    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateClientProfile = (updated: Partial<ClientProfile>) => {
    if (!clientProfile) return;
    setClientProfile({ ...clientProfile, ...updated });
  };

  const isSelf = user?.id === id;
  const fmt = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR') : '—';

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'dados', label: 'Dados e Endereço', icon: 'ri-user-3-line' },
    { id: 'acesso', label: 'Acesso e Permissões', icon: 'ri-key-2-line' },
    { id: 'visao-geral', label: 'Visão Geral do App', icon: 'ri-dashboard-line' },
    { id: 'assinatura', label: 'Plano & Assinatura', icon: 'ri-vip-crown-line' },
    { id: 'pagamentos', label: 'Pagamentos', icon: 'ri-bank-card-line' },
    { id: 'auditoria', label: 'Auditoria', icon: 'ri-history-line' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
    </div>
  );

  if (notFound || !admin) return (
    <div className="flex flex-col items-center justify-center py-32 text-center">
      <i className="ri-shield-user-line text-[48px] text-white/20 mb-4" />
      <p className="text-white text-[18px] font-600">Administrador não encontrado</p>
      <p className="text-white/40 text-[13px] mt-2">Este usuário não tem papel administrativo ou não existe.</p>
      <button onClick={() => navigate('/admin/administradores')} className="mt-5 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors">
        <i className="ri-arrow-left-line mr-1.5" />Voltar à lista
      </button>
    </div>
  );

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 text-[13px]">
        <button
          onClick={() => navigate('/admin/administradores')}
          className="text-white/40 hover:text-white cursor-pointer transition-colors flex items-center gap-1.5"
        >
          <i className="ri-arrow-left-line" />
          Administradores
        </button>
        <i className="ri-arrow-right-s-line text-white/20" />
        <span className="text-white/70">{admin.full_name}</span>
        <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-white/40">
          {roleLabel[admin.role]}
        </span>
      </div>

      {/* Header card */}
      <AdminDetailHeader admin={admin} isSelf={isSelf} onUpdate={load} />

      {/* Full Access badge */}
      <div className="mt-4 flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
        <i className="ri-shield-star-fill text-emerald-400 text-[16px]" />
        <div className="flex-1">
          <span className="text-[13px] font-600 text-emerald-300">Acesso Total (Admin)</span>
          <span className="text-[12px] text-white/40 ml-2">— todas as funcionalidades e limites ilimitados no app estão liberados para esta conta</span>
        </div>
        <span className="text-[11px] bg-emerald-500/20 text-emerald-400 px-2.5 py-1 rounded-full font-600">Full Access</span>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1 w-fit mt-5 mb-5 px-1 py-1 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-600 transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id ? 'bg-violet-600 text-white' : 'text-white/50 hover:text-white'
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'dados' && (
        <AdminPersonalSection admin={admin} onUpdate={load} />
      )}

      {activeTab === 'acesso' && (
        <div className="space-y-5">
          <AdminAccessSection admin={admin} isSelf={isSelf} onUpdate={load} onRemoved={() => navigate('/admin/administradores')} />
          <AdminModulePermissions admin={admin} isSelf={isSelf} onUpdate={load} />
          {!isSelf && (
            <AdminConvertToUserSection admin={admin} />
          )}
        </div>
      )}

      {activeTab === 'visao-geral' && clientProfile && (
        <div className="space-y-5">
          {/* Admin plan info banner */}
          <div className="p-4 bg-violet-600/10 border border-violet-500/20 rounded-xl flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-violet-600/20 rounded-xl shrink-0">
              <i className="ri-shield-star-line text-violet-400 text-[16px]" />
            </div>
            <div>
              <p className="text-[13px] font-600 text-violet-300">Conta administrativa com acesso Full</p>
              <p className="text-[12px] text-white/40 mt-0.5">Os limites exibidos abaixo refletem o uso real no app. Esta conta não está sujeita a restrições de plano.</p>
            </div>
          </div>
          <OverviewTab profile={clientProfile} subscription={subscription} usage={usage} plans={plans} />
        </div>
      )}

      {activeTab === 'assinatura' && clientProfile && (
        <div className="space-y-5">
          {/* Admin full access info */}
          <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-emerald-500/20 rounded-xl shrink-0">
                <i className="ri-shield-star-fill text-emerald-400 text-[16px]" />
              </div>
              <div>
                <p className="text-[13px] font-600 text-emerald-300">Acesso Total Administrativo</p>
                <p className="text-[12px] text-white/40 mt-0.5">
                  Esta conta tem acesso irrestrito a todas as funcionalidades do app independente do plano configurado abaixo.
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 ml-4">
              <p className="text-[11px] text-white/30">Papel</p>
              <p className="text-[13px] font-700 text-emerald-400">{roleLabel[admin.role]}</p>
            </div>
          </div>
          <SubscriptionTab
            profile={clientProfile}
            subscription={subscription}
            plans={plans}
            onUpdate={updateClientProfile}
            onRefresh={load}
          />
        </div>
      )}

      {activeTab === 'pagamentos' && (
        <PaymentsTab payments={payments} />
      )}

      {activeTab === 'auditoria' && (
        <div className="space-y-5">
          {/* Admin timeline using client component */}
          {clientProfile && (
            <TimelineTab profile={clientProfile} payments={payments} />
          )}

          {/* Admin specific audit */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
                <i className="ri-shield-check-line text-white/60 text-[15px]" />
              </div>
              <h3 className="text-[15px] font-600 text-white">Informações Administrativas</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Adicionado como admin', val: fmt(admin.createdAt) },
                { label: 'Convidado em', val: fmt(admin.invitedAt) },
                { label: 'Último acesso ao painel', val: fmt(admin.last_seen_at) },
                { label: 'Papel atual', val: roleLabel[admin.role] || admin.role },
                { label: 'Status', val: admin.status === 'active' ? 'Ativo' : 'Inativo' },
                { label: 'ID do usuário', val: admin.userId },
              ].map((item) => (
                <div key={item.label} className="bg-white/3 rounded-xl p-4 border border-white/5">
                  <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-1.5">{item.label}</p>
                  <p className="text-[13px] text-white break-all">{item.val}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-4 bg-white/3 rounded-xl border border-white/5">
              <p className="text-[12px] text-white/30 mb-2"><i className="ri-information-line mr-1.5" />Log de auditoria detalhado</p>
              <p className="text-[12px] text-white/50">
                Para ver o histórico completo de ações deste administrador, acesse{' '}
                <button
                  onClick={() => navigate('/admin/auditoria')}
                  className="text-violet-400 hover:underline cursor-pointer"
                >
                  Auditoria do Sistema
                </button>
                {' '}e filtre pelo e-mail <strong className="text-white/70">{admin.email}</strong>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdministradorDetail;
